/**
 * 订单确认（一口价下单）
 * 牛只只读卡 + 订单表单 + 金额结算（IR-14 费率）+ 合同签署 + 支付方式
 * 提交锁牛 15 分钟（IR-05）
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCattle } from '@/api/cattle';
import { createOrder } from '@/api/trade';
import { useAuth } from '@/stores/auth';
import type { Cattle, ContractData } from '@/types';
import {
  FREE_FEE_USER_TYPES,
  LOCK_MINUTES,
  SERVICE_FEE_MIN,
  SERVICE_FEE_RATE_AUCTION,
  SERVICE_FEE_RATE_FIXED,
  imgFallback,
} from '@/utils/constants';
import { formatDateTime, formatMoney, formatWeight } from '@/utils/format';
import ContractPreview from '@/components/common/ContractPreview';
import Loading from '@/components/common/Loading';
import ErrorBox from '@/components/common/ErrorBox';
import '@/styles/pages/checkout.css';

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const cattleId = Number(searchParams.get('cattle_id'));
  // B-03 拍卖下单：/checkout?cattle_id=X&order_type=3&auction_id=Y&deal_price=Z（AuctionDetail 中标跳转）
  const orderType = Number(searchParams.get('order_type')) || 1; // 1-现货 2-预售 3-拍卖
  const auctionId = Number(searchParams.get('auction_id')) || undefined;
  const dealPrice = Number(searchParams.get('deal_price')) || undefined;
  const navigate = useNavigate();
  const { user } = useAuth();

  const [cattle, setCattle] = useState<Cattle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 表单
  const [quantity, setQuantity] = useState(1);
  const [deliveryMethod, setDeliveryMethod] = useState(1);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [remark, setRemark] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(1);
  const [agree, setAgree] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!cattleId) {
      setError('缺少牛只参数');
      setLoading(false);
      return;
    }
    getCattle(cattleId)
      .then((res) => {
        setCattle(res.cattle);
        setQuantity(1);
      })
      .catch((e) => setError(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  }, [cattleId]);

  // 金额结算（IR-14：一口价 1.5% 最低 50 元 / 拍卖 2.0% 无最低；散户免佣）
  // 拍卖单（type=3）单价以成交价 deal_price 快照为准
  const unitPrice = orderType === 3 && dealPrice ? dealPrice : (cattle?.listing_price ?? 0);
  const subtotal = useMemo(() => unitPrice * quantity, [unitPrice, quantity]);
  const isFreeFee = FREE_FEE_USER_TYPES.includes(user?.user_type ?? -1);
  const serviceFee = useMemo(() => {
    if (isFreeFee) return 0;
    if (orderType === 3) return subtotal * SERVICE_FEE_RATE_AUCTION; // 拍卖 2.0% 无最低
    const raw = subtotal * SERVICE_FEE_RATE_FIXED;
    return Math.max(raw, SERVICE_FEE_MIN);
  }, [subtotal, isFreeFee, orderType]);
  const total = subtotal + serviceFee;

  const maxQty = cattle?.available_qty ?? 1;

  const contract: ContractData = useMemo(
    () => ({
      order_no: 'ORD-{日期}-{10位随机}',
      seller_name: cattle?.seller?.enterprise_name || cattle?.seller?.real_name || cattle?.farm_name || '卖方',
      buyer_name: user?.real_name || user?.username || '买方',
      cattle_desc: `${cattle?.breed ?? ''} · ${cattle?.ear_tag_no ?? ''}（${formatWeight(cattle?.weight)}）`,
      quantity,
      unit_price: unitPrice,
      total_amount: subtotal,
      service_fee: serviceFee,
      delivery_address: deliveryAddress || '待填写',
      delivery_time: deliveryTime || '双方协商',
      created_at: formatDateTime(new Date().toISOString()),
    }),
    [cattle, user, quantity, unitPrice, subtotal, serviceFee, deliveryAddress, deliveryTime],
  );

  const onSubmit = async () => {
    setSubmitError('');
    if (!cattle) return;
    if (quantity < 1 || quantity > maxQty) {
      setSubmitError(`购买数量需在 1~${maxQty} 头之间`);
      return;
    }
    if (!contactName.trim() || !contactPhone.trim()) {
      setSubmitError('请填写联系人与联系电话');
      return;
    }
    if (!agree) {
      setSubmitError('请先阅读并同意电子买卖合同');
      return;
    }
    setSubmitting(true);
    try {
      const res = await createOrder({
        order_type: orderType,
        buyer_id: user?.id ?? 0,
        seller_id: cattle.seller?.id ?? 0,
        items: [{ cattle_id: cattle.id, unit_price: unitPrice, quantity }],
        payment_method: paymentMethod,
        delivery_method: deliveryMethod,
        delivery_address: deliveryAddress,
        delivery_time: deliveryTime,
        contact_name: contactName,
        contact_phone: contactPhone,
        remark,
        ...(orderType === 3 ? { auction_id: auctionId, deal_price: dealPrice } : {}),
      });
      navigate(`/orders/${res.order_id}`);
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : '下单失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container checkout-page">
        <Loading rows={4} />
      </div>
    );
  }

  if (error || !cattle) {
    return (
      <div className="container checkout-page">
        <ErrorBox message={error || '牛只不存在'} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return (
    <div className="checkout-page container">
      <h1 className="checkout-title">订单确认</h1>

      <div className="checkout-layout">
        {/* 左：牛只只读卡 + 表单 */}
        <div className="checkout-main">
          <div className="checkout-card">
            <div className="checkout-card__head">牛只信息（只读）</div>
            <div className="checkout-cattle">
              {cattle.images?.[0] || cattle.face_image_url ? (
                <img
                  src={cattle.images?.[0] ?? cattle.face_image_url}
                  alt={cattle.breed}
                  data-local="/images/cattle-default.jpg"
                  onError={imgFallback}
                />
              ) : (
                <div className="checkout-cattle__placeholder">🐂</div>
              )}
              <div className="checkout-cattle__info">
                <strong>{cattle.breed} · {cattle.ear_tag_no}</strong>
                <span>{formatWeight(cattle.weight)} · {cattle.age_days} 日龄</span>
                <span>挂牌价 {formatMoney(cattle.listing_price)}/头 · 可售 {maxQty} 头</span>
              </div>
            </div>
          </div>

          <div className="checkout-card">
            <div className="checkout-card__head">交割信息</div>
            <div className="form-group">
              <label className="form-label"><span className="req">*</span>购买数量（头）</label>
              <input
                className="form-control"
                type="number"
                min={1}
                max={maxQty}
                value={quantity}
                onChange={(e) => setQuantity(Number(e.target.value))}
              />
              <div className="form-help">最多可购 {maxQty} 头</div>
            </div>
            <div className="form-group">
              <label className="form-label"><span className="req">*</span>交割方式</label>
              <div className="form-radio-row">
                {[
                  [1, '现场验收'],
                  [2, '远程视频验收'],
                ].map(([v, l]) => (
                  <label className="form-radio" key={v}>
                    <input
                      type="radio"
                      name="delivery_method"
                      checked={deliveryMethod === v}
                      onChange={() => setDeliveryMethod(v as number)}
                    />
                    {l}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">交割地点</label>
              <input
                className="form-control"
                placeholder="请填写交割地点（GPS 校验范围依据）"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">期望交割时间</label>
              <input
                className="form-control"
                type="datetime-local"
                value={deliveryTime}
                onChange={(e) => setDeliveryTime(e.target.value)}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label"><span className="req">*</span>联系人</label>
                <input
                  className="form-control"
                  placeholder="联系人姓名"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label"><span className="req">*</span>联系电话</label>
                <input
                  className="form-control"
                  placeholder="联系电话"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">备注</label>
              <textarea
                className="form-control"
                placeholder="补充说明（选填）"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
            </div>
          </div>

          <div className="checkout-card">
            <div className="checkout-card__head">支付方式</div>
            <div className="form-radio-row">
              {[
                [1, '微信支付'],
                [2, '对公转账'],
              ].map(([v, l]) => (
                <label className="form-radio" key={v}>
                  <input
                    type="radio"
                    name="payment_method"
                    checked={paymentMethod === v}
                    onChange={() => setPaymentMethod(v as number)}
                  />
                  {l}
                </label>
              ))}
            </div>
            <div className="form-help">资金进入平台监管账户，交割确认后解付卖家（ST-07）</div>
          </div>
        </div>

        {/* 右：金额结算 + 合同 + 提交 */}
        <aside className="checkout-side">
          <div className="checkout-card">
            <div className="checkout-card__head">金额结算</div>
            <div className="settle-row">
              <span>挂牌价小计（{quantity} 头）</span>
              <b>{formatMoney(subtotal)}</b>
            </div>
            <div className="settle-row">
              <span>平台服务费（{orderType === 3 ? '竞价 2.0%' : '一口价 1.5%，最低 50 元'}）</span>
              <b>{isFreeFee ? '免收（散户优惠）' : formatMoney(serviceFee)}</b>
            </div>
            <div className="settle-row settle-row--total">
              <span>应付合计</span>
              <b>{formatMoney(total)}</b>
            </div>
            <div className="settle-tip">
              竞价成交服务费率 2.0%；大宗议价（≥50 头）面议
            </div>
          </div>

          <div className="checkout-card">
            <div className="checkout-card__head">电子合同</div>
            <button className="btn btn-outline btn-block" onClick={() => setPreviewOpen(true)}>
              📄 预览电子买卖合同
            </button>
            <label className="form-check checkout-agree">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
              我已阅读并同意签署电子买卖合同
            </label>
          </div>

          <div className="checkout-card checkout-submit">
            <div className="checkout-lock-tip">
              ⏱ 提交后牛只将锁定 <b>{LOCK_MINUTES} 分钟</b>，超时未支付自动释放
            </div>
            {submitError ? <div className="form-error">{submitError}</div> : null}
            <button className="btn btn-primary btn-block btn-lg" disabled={submitting} onClick={onSubmit}>
              {submitting ? '提交中…' : '提交订单并锁定牛只'}
            </button>
          </div>
        </aside>
      </div>

      <ContractPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="电子买卖合同预览"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setPreviewOpen(false)}>关闭</button>
            <button
              className="btn btn-primary"
              onClick={() => {
                setAgree(true);
                setPreviewOpen(false);
              }}
            >
              我已阅读并同意
            </button>
          </>
        }
      >
        <h4>云牛集牛只电子买卖合同</h4>
        <p>
          合同编号：{contract.order_no}
        </p>
        <p>卖方：{contract.seller_name}</p>
        <p>买方：{contract.buyer_name}</p>
        <p>
          双方就牛只（{contract.cattle_desc}，数量 {contract.quantity} 头，单价{' '}
          {formatMoney(contract.unit_price)}）达成如下交易：
        </p>
        <h4>一、交易金额</h4>
        <p>
          牛只价款小计 {formatMoney(contract.total_amount)} 元；平台服务费{' '}
          {formatMoney(contract.service_fee)} 元；合计应付 {formatMoney(contract.total_amount + contract.service_fee)} 元。
        </p>
        <h4>二、交割条款</h4>
        <p>交割地点：{contract.delivery_address}</p>
        <p>交割时间：{contract.delivery_time}</p>
        <p>交割确认需上传不少于 3 张现场照片/视频凭证，并携带 GPS 信息以供校验。</p>
        <h4>三、资金监管</h4>
        <p>买方付款进入平台监管账户，交割确认后平台解付卖方；争议交易进入仲裁流程。</p>
        <h4>四、存证</h4>
        <p>本合同与交割凭证将同步上链存证，支持公开查验。</p>
        <div className="contract-sign">
          签署时间：{contract.created_at} · 本预览内容为示意，正式合同以平台生成的电子合同为准
        </div>
      </ContractPreview>
    </div>
  );
}
